import * as THREE from 'three';
import { applyMergedChunks } from './mergedMaterial';

/**
 * Shader patches for creature/character materials.
 *
 * - **Rim**: a fresnel edge on every character (a cool rim by day, tuned per
 *   zone look) so bodies separate from the ground in the quarter view.
 * - **Glow**: per-actor rim + body colours for gameplay states (counter
 *   window, stagger, enrage, identity modes, hit flash). Values above 1 bloom.
 * - **Battle outline**: an inverted hull extruded a constant number of pixels
 *   in screen space (smoothed normals, so flat-shaded low-poly bodies keep a
 *   closed silhouette). Bosses get a red one; it turns blue during counter
 *   windows.
 *
 * All actors share two programs (`af-actor`, `af-outline`); per-actor values
 * live in uniforms, so a hundred monsters never trigger extra compiles.
 */

/** Rim colour shared by every actor (set from the zone look). */
export const SHARED_RIM = { value: new THREE.Color(0.12, 0.16, 0.22) };
/** Drawing-buffer size in pixels (outline width is in pixels). */
export const OUTLINE_VIEWPORT = { value: new THREE.Vector2(1920, 1080) };

export interface ActorUniforms {
  uGlowRim: { value: THREE.Color };
  uGlowBody: { value: THREE.Color };
  /** Fresnel exponent of the glow rim (lower = wider). */
  uGlowPow: { value: number };
  /** Glow-accent hue override for tinted variants (merged materials). */
  uEmTint: { value: THREE.Color };
  uEmMode: { value: number };
}

export function actorUniforms(): ActorUniforms {
  return {
    uGlowRim: { value: new THREE.Color(0, 0, 0) },
    uGlowBody: { value: new THREE.Color(0, 0, 0) },
    uGlowPow: { value: 2 },
    uEmTint: { value: new THREE.Color(1, 1, 1) },
    uEmMode: { value: 0 },
  };
}

/** Adds rim + glow terms to a lit material (MeshStandard/Physical). */
export function patchActorMaterial(mat: THREE.Material, u: ActorUniforms): void {
  if (!(mat instanceof THREE.MeshStandardMaterial)) return;
  const merged = mat.userData.merged === true;
  mat.onBeforeCompile = (shader) => {
    if (merged) applyMergedChunks(shader, u);
    shader.uniforms.uRimBase = SHARED_RIM;
    shader.uniforms.uGlowRim = u.uGlowRim;
    shader.uniforms.uGlowBody = u.uGlowBody;
    shader.uniforms.uGlowPow = u.uGlowPow;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uRimBase;\nuniform vec3 uGlowRim;\nuniform vec3 uGlowBody;\nuniform float uGlowPow;')
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        float afNdv = 1.0 - saturate(dot(normal, normalize(vViewPosition)));
        totalEmissiveRadiance += uRimBase * pow(afNdv, 3.0) + uGlowRim * pow(afNdv, uGlowPow) + uGlowBody;`,
      );
  };
  mat.customProgramCacheKey = () => (merged ? 'af-actor-m' : 'af-actor');
  mat.needsUpdate = true;
}

export interface OutlineUniforms {
  uOutlineColor: { value: THREE.Color };
  /** Width in drawing-buffer pixels (0 hides it). */
  uOutlinePx: { value: number };
}

export function outlineUniforms(): OutlineUniforms {
  return { uOutlineColor: { value: new THREE.Color(1.0, 0.12, 0.05) }, uOutlinePx: { value: 2.5 } };
}

function outlineMaterial(u: OutlineUniforms): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide, toneMapped: false });
  m.onBeforeCompile = (s) => {
    s.uniforms.uOutlineColor = u.uOutlineColor;
    s.uniforms.uOutlinePx = u.uOutlinePx;
    s.uniforms.uViewport = OUTLINE_VIEWPORT;
    s.vertexShader = s.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 afOutlineNormal;\nuniform float uOutlinePx;\nuniform vec2 uViewport;')
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        {
          vec3 afN = afOutlineNormal;
          #ifdef USE_SKINNING
            afN = (skinMatrix * vec4(afN, 0.0)).xyz;
          #endif
          vec3 afView = normalize(normalMatrix * afN);
          vec2 afDir = (projectionMatrix * vec4(afView, 0.0)).xy;
          float afLen = length(afDir);
          if (afLen > 1e-5) gl_Position.xy += (afDir / afLen) * uOutlinePx * 2.0 / uViewport * gl_Position.w;
        }`,
      );
    s.fragmentShader = s.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uOutlineColor;')
      .replace('vec4 diffuseColor = vec4( diffuse, opacity );', 'vec4 diffuseColor = vec4( uOutlineColor, opacity );');
  };
  m.customProgramCacheKey = () => 'af-outline';
  return m;
}

/**
 * Normals averaged over coincident vertices (flat shading splits them), so
 * the extruded hull stays closed. Computed once per geometry and shared by
 * every instance of the model.
 */
function ensureOutlineNormals(geo: THREE.BufferGeometry): void {
  if (geo.getAttribute('afOutlineNormal')) return;
  const pos = geo.getAttribute('position');
  const nrm = geo.getAttribute('normal');
  if (!pos || !nrm) return;
  const acc = new Map<string, [number, number, number]>();
  const keys: string[] = new Array<string>(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const k = `${Math.round(pos.getX(i) * 4096)},${Math.round(pos.getY(i) * 4096)},${Math.round(pos.getZ(i) * 4096)}`;
    keys[i] = k;
    let a = acc.get(k);
    if (!a) {
      a = [0, 0, 0];
      acc.set(k, a);
    }
    a[0] += nrm.getX(i);
    a[1] += nrm.getY(i);
    a[2] += nrm.getZ(i);
  }
  const out = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const a = acc.get(keys[i] as string) as [number, number, number];
    const l = Math.hypot(a[0], a[1], a[2]) || 1;
    out[i * 3] = a[0] / l;
    out[i * 3 + 1] = a[1] / l;
    out[i * 3 + 2] = a[2] / l;
  }
  geo.setAttribute('afOutlineNormal', new THREE.BufferAttribute(out, 3));
}

/**
 * Builds outline hulls for every skinned mesh under `model` (sharing geometry
 * and skeleton with the source mesh). Returns the created meshes and their
 * material, to be disposed with the view.
 */
export function addOutline(model: THREE.Object3D, u: OutlineUniforms): { meshes: THREE.Mesh[]; material: THREE.Material } {
  const material = outlineMaterial(u);
  const sources: THREE.SkinnedMesh[] = [];
  model.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) sources.push(o as THREE.SkinnedMesh);
  });
  const meshes: THREE.Mesh[] = [];
  for (const src of sources) {
    ensureOutlineNormals(src.geometry);
    if (!src.geometry.getAttribute('afOutlineNormal')) continue;
    const hull = new THREE.SkinnedMesh(src.geometry, material);
    hull.name = `${src.name}_outline`;
    hull.bind(src.skeleton, src.bindMatrix);
    hull.bindMode = src.bindMode;
    hull.position.copy(src.position);
    hull.quaternion.copy(src.quaternion);
    hull.scale.copy(src.scale);
    hull.castShadow = false;
    hull.receiveShadow = false;
    if (src.boundingSphere) hull.boundingSphere = src.boundingSphere.clone();
    hull.frustumCulled = src.frustumCulled;
    src.parent?.add(hull);
    meshes.push(hull);
  }
  return { meshes, material };
}
